import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Activity,
  ArrowLeft,
  Brain,
  Loader2,
  MessageSquareQuote,
  RotateCcw,
  Sparkles,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";

const PERIOD_OPTIONS = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const FALLBACK_QUESTIONS = [
  "How do I lift intensity next?",
  "Which game fits best?",
  "How do I keep fun high?",
  "What should I say before training?",
];

export default function AIAdvicePage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user } = useUser();
  const messagesEndRef = useRef(null);
  const isCoach = user?.account_type === "coach";

  const [advicePeriod, setAdvicePeriod] = useState("month");
  const [adviceData, setAdviceData] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [starterQuestions, setStarterQuestions] = useState(FALLBACK_QUESTIONS);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");

  const normalizeChatMessages = (messages = []) => {
    if (!messages.length) return [];
    const hasUserMessages = messages.some((message) => message.role === "user");
    if (hasUserMessages && messages[0]?.role === "assistant") {
      return messages.slice(1);
    }
    return messages;
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatLoading]);

  useEffect(() => {
    if (!sessionId) {
      setSessionId(`advice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    }
  }, [sessionId]);

  useEffect(() => {
    if (user?.id && isCoach && sessionId) {
      void fetchTrainingAdvice(advicePeriod, sessionId);
    }
  }, [user?.id, isCoach, advicePeriod, sessionId]);

  const fetchTrainingAdvice = async (period, activeSessionId) => {
    if (!user?.id || !activeSessionId) return;
    setAdviceLoading(true);
    try {
      const response = await axios.post(`${API}/coach/training-advice-detailed`, {
        coach_id: user.id,
        period,
        session_id: activeSessionId,
      });
      setAdviceData(response.data);
      setStarterQuestions(response.data.starter_questions || FALLBACK_QUESTIONS);
      setChatMessages([]);
    } catch (error) {
      console.error("Error fetching advice:", error);
      toast.error(error.response?.data?.detail || "Could not load training advice");
    } finally {
      setAdviceLoading(false);
    }
  };

  const sendMessage = async (selectedQuestion) => {
    if (chatLoading || !user?.id || !sessionId) return;

    const userMessage = selectedQuestion?.trim();
    if (!userMessage) return;

    setChatMessages((prev) => [
      ...prev,
      { role: "user", text: userMessage, created_at: new Date().toISOString() },
    ]);
    setChatLoading(true);

    try {
      const response = await axios.post(`${API}/coach/training-advice-chat`, {
        coach_id: user.id,
        session_id: sessionId,
        message: userMessage,
        period: advicePeriod,
      });
      setAdviceData((prev) => ({
        ...(prev || {}),
        summary: response.data.summary || prev?.summary,
      }));
      setStarterQuestions(response.data.starter_questions || FALLBACK_QUESTIONS);
      setChatMessages(normalizeChatMessages(response.data.messages || []));
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.detail || "Could not send message");
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Sorry, I encountered an error. Please try again.",
          created_at: new Date().toISOString(),
          isError: true,
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleNewSession = () => {
    setSessionId(`advice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    setAdviceData(null);
    setChatMessages([]);
  };

  const pageInsights = useMemo(() => {
    const summary = adviceData?.summary;
    if (!summary) return null;

    const topSessionType = summary.top_session_types?.[0]?.type;
    const adviceLines = (adviceData?.advice || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const adviceIntro = adviceLines[0] || "You’re building from a good base, so the next step is to keep things simple.";
    const adviceBullets = (adviceLines.length > 1
      ? adviceLines.slice(1)
      : [
          `Keep one short block that protects ${summary.highest_area_display.toLowerCase()}.`,
          `Add one focused drill that directly improves ${summary.lowest_area_display.toLowerCase()}.`,
        ]).map((line) => line.replace(/^[-•]\s*/, ""));

    return {
      strengths: [
        `${summary.highest_area_display} is your clearest strength right now.`,
        topSessionType
          ? `${topSessionType} is the session type your group sees most often.`
          : `${summary.active_athletes} athletes have been active in this period.`,
      ],
      weaknesses: [
        `${summary.lowest_area_display} needs the most attention next.`,
        summary.completion_rate < 70
          ? `Only ${summary.completion_rate}% of sessions were rated, so the picture is still a bit thin.`
          : `Feedback is coming in well, but this is still the area that needs lifting.`,
      ],
      adviceIntro,
      adviceBullets,
    };
  }, [adviceData?.advice, adviceData?.summary]);

  if (!isCoach) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center p-4`}>
        <div className="text-center" data-testid="ai-advice-locked-state">
          <Brain className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
          <h2 className={`text-xl font-bold mb-2 ${theme.textPrimary}`}>Coach Feature</h2>
          <p className={theme.textMuted}>This feature is only available for coaches.</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-6 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white rounded-xl"
            data-testid="ai-advice-go-home-button"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col`} data-testid="ai-advice-page">
      <header className="flex items-center justify-between p-4 pt-6">
        <button
          onClick={() => navigate(-1)}
          className={`w-10 h-10 rounded-full flex items-center justify-center ${theme.button}`}
          data-testid="ai-advice-back-button"
        >
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <div className="flex items-center gap-2" data-testid="ai-advice-header-title">
          <h1 className={`text-lg font-bold ${theme.textPrimary}`}>My AI coach</h1>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            BETA
          </span>
        </div>
        <button
          onClick={handleNewSession}
          className={`w-10 h-10 rounded-full flex items-center justify-center ${theme.button}`}
          data-testid="ai-advice-new-session-button"
        >
          <RotateCcw className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
      </header>

      <div className="px-4 pb-3">
        <div className="flex gap-2" data-testid="ai-advice-period-switcher">
          {PERIOD_OPTIONS.map((period) => (
            <button
              key={period.value}
              onClick={() => setAdvicePeriod(period.value)}
              className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                advicePeriod === period.value
                  ? "bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white"
                  : isDark
                    ? "bg-white/10 text-white/70"
                    : "bg-[#493986]/10 text-[#493986]/70"
              }`}
              data-testid={`ai-advice-period-${period.value}`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {adviceLoading && chatMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16" data-testid="ai-advice-loading-state">
            <Loader2 className={`w-10 h-10 animate-spin ${isDark ? "text-purple-400" : "text-[#493986]"}`} />
            <p className={`mt-4 text-sm ${theme.textMuted}`}>Just a moment — I’m putting together something helpful.</p>
          </div>
        ) : chatMessages.length === 0 ? (
          <div className="space-y-3" data-testid="ai-advice-empty-state">
            <div
              className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-white/5" : "border-[#493986]/10 bg-white/80"}`}
              data-testid="ai-advice-strengths-section"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-2xl ${isDark ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
                  <Sparkles className="w-4 h-4 text-emerald-500" />
                </div>
                <h2 className={`text-base font-semibold ${theme.textPrimary}`}>Strengths</h2>
              </div>
              <div className="space-y-2">
                {(pageInsights?.strengths || []).map((item, index) => (
                  <p key={`strength-${index}`} className={`text-sm leading-6 ${theme.textMuted}`} data-testid={`ai-advice-strength-item-${index}`}>
                    • {item}
                  </p>
                ))}
              </div>
            </div>

            <div
              className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-white/5" : "border-[#493986]/10 bg-white/80"}`}
              data-testid="ai-advice-weaknesses-section"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-2xl ${isDark ? "bg-amber-500/20" : "bg-amber-100"}`}>
                  <Target className="w-4 h-4 text-amber-500" />
                </div>
                <h2 className={`text-base font-semibold ${theme.textPrimary}`}>Weaknesses</h2>
              </div>
              <div className="space-y-2">
                {(pageInsights?.weaknesses || []).map((item, index) => (
                  <p key={`weakness-${index}`} className={`text-sm leading-6 ${theme.textMuted}`} data-testid={`ai-advice-weakness-item-${index}`}>
                    • {item}
                  </p>
                ))}
              </div>
            </div>

            <div
              className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-white/5" : "border-[#493986]/10 bg-white/80"}`}
              data-testid="ai-advice-simple-advice-section"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-2xl ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}>
                  <Activity className="w-4 h-4 text-blue-500" />
                </div>
                <h2 className={`text-base font-semibold ${theme.textPrimary}`}>Simple advice</h2>
              </div>
              <p className={`text-sm leading-6 ${theme.textMuted}`} data-testid="ai-advice-simple-advice-intro">
                {pageInsights?.adviceIntro}
              </p>
              <div className="space-y-2">
                {(pageInsights?.adviceBullets || []).map((item, index) => (
                  <p key={`advice-${index}`} className={`text-sm leading-6 ${theme.textMuted}`} data-testid={`ai-advice-simple-advice-item-${index}`}>
                    • {item}
                  </p>
                ))}
              </div>
            </div>

            <p className={`px-1 pt-1 text-sm ${theme.textMuted}`} data-testid="ai-advice-follow-up-note">
              Want a bit more help? Choose one of the questions below.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {pageInsights && (
              <div className="space-y-3 pb-2">
                <div
                  className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-white/5" : "border-[#493986]/10 bg-white/80"}`}
                  data-testid="ai-advice-strengths-section"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-2xl ${isDark ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
                      <Sparkles className="w-4 h-4 text-emerald-500" />
                    </div>
                    <h2 className={`text-base font-semibold ${theme.textPrimary}`}>Strengths</h2>
                  </div>
                  <div className="space-y-2">
                    {pageInsights.strengths.map((item, index) => (
                      <p key={`strength-chat-${index}`} className={`text-sm leading-6 ${theme.textMuted}`} data-testid={`ai-advice-strength-item-${index}`}>
                        • {item}
                      </p>
                    ))}
                  </div>
                </div>

                <div
                  className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-white/5" : "border-[#493986]/10 bg-white/80"}`}
                  data-testid="ai-advice-weaknesses-section"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-2xl ${isDark ? "bg-amber-500/20" : "bg-amber-100"}`}>
                      <Target className="w-4 h-4 text-amber-500" />
                    </div>
                    <h2 className={`text-base font-semibold ${theme.textPrimary}`}>Weaknesses</h2>
                  </div>
                  <div className="space-y-2">
                    {pageInsights.weaknesses.map((item, index) => (
                      <p key={`weakness-chat-${index}`} className={`text-sm leading-6 ${theme.textMuted}`} data-testid={`ai-advice-weakness-item-${index}`}>
                        • {item}
                      </p>
                    ))}
                  </div>
                </div>

                <div
                  className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-white/5" : "border-[#493986]/10 bg-white/80"}`}
                  data-testid="ai-advice-simple-advice-section"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-2xl ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}>
                      <Activity className="w-4 h-4 text-blue-500" />
                    </div>
                    <h2 className={`text-base font-semibold ${theme.textPrimary}`}>Simple advice</h2>
                  </div>
                  <p className={`text-sm leading-6 ${theme.textMuted}`} data-testid="ai-advice-simple-advice-intro">
                    {pageInsights.adviceIntro}
                  </p>
                  <div className="space-y-2">
                    {pageInsights.adviceBullets.map((item, index) => (
                      <p key={`advice-chat-${index}`} className={`text-sm leading-6 ${theme.textMuted}`} data-testid={`ai-advice-simple-advice-item-${index}`}>
                        • {item}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {chatMessages.map((message, index) => (
              <div
                key={`${message.role}-${message.created_at || index}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`ai-advice-message-row-${index}`}
              >
                <div
                  className={`max-w-[88%] rounded-[28px] p-4 ${
                    message.role === "user"
                      ? "bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white"
                      : message.isError
                        ? isDark
                          ? "bg-red-500/20 text-red-300"
                          : "bg-red-100 text-red-700"
                        : isDark
                          ? "bg-white/10 text-white"
                          : "bg-white/85 text-[#1b1536] shadow-sm"
                  }`}
                  data-testid={`ai-advice-chat-message-${index}`}
                >
                  {message.role === "assistant" && (
                    <div className="mb-3 flex items-center gap-2">
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full ${isDark ? "bg-purple-500/30" : "bg-[#493986]/15"}`}>
                        <Brain className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-semibold opacity-70">MyGoal helper</span>
                    </div>
                  )}
                  <div className="text-sm leading-7 whitespace-pre-wrap">{message.text || message.content}</div>
                </div>
              </div>
            ))}

            {chatLoading && (
              <div className="flex justify-start" data-testid="ai-advice-chat-loading">
                <div className={`rounded-2xl p-4 ${isDark ? "bg-white/10" : "bg-[#493986]/10"}`}>
                  <div className="flex items-center gap-2">
                    <Loader2 className={`w-4 h-4 animate-spin ${theme.textMuted}`} />
                    <span className={`text-sm ${theme.textMuted}`}>Working on it...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="px-4 pb-3">
        <p className={`mb-2 text-xs uppercase tracking-[0.18em] ${theme.textMuted}`} data-testid="ai-advice-question-picker-label">
          Choose your next question
        </p>
        <div className="flex flex-wrap gap-2 pb-2" data-testid="ai-advice-starter-questions">
          {(starterQuestions.length ? starterQuestions : FALLBACK_QUESTIONS).map((question, index) => (
            <button
              key={`${question}-${index}`}
              onClick={() => sendMessage(question)}
              className={`rounded-full border px-3 py-2 text-xs font-medium transition-colors ${
                isDark
                  ? "border-white/20 text-white/70 hover:bg-white/10"
                  : "border-[#493986]/20 text-[#493986]/80 hover:bg-[#493986]/10"
              }`}
              data-testid={`ai-advice-starter-question-${index}`}
            >
              <span className="inline-flex items-center gap-2">
                <MessageSquareQuote className="w-3.5 h-3.5" />
                {question}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
